/* eslint-disable react-hooks/exhaustive-deps */
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback, useLayoutEffect } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { apiRefresh } from './auth/index';
import { captureException } from '@sentry/react';
import { axiosLogger } from 'Axioslogger';
import { usePopup } from 'context/PopupContext/PopupContext';

export const tzAxios = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  validateStatus(status) {
    return status >= 200 && status <= 201;
  },
  headers: {
    'X-Custom-Language': 'ar',
  },
});

tzAxios.interceptors.request.use((request) => {
  axiosLogger('Request for Axios Logger: ', request);
  return request;
});

// Response interceptor
tzAxios.interceptors.response.use(
  (response) => {
    axiosLogger('Response for Axios Logger: ', response);
    return response;
  },
  (error) => {
    axiosLogger('Error Response:', error.response);
    captureException(error);
    return Promise.reject(error);
  },
);

export const useInterceptors = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { showPopup } = usePopup();

  const handleResponseSuccess = (response: AxiosResponse) => response;

  const handleErrorCodes = useCallback(
    async (error: AxiosError<{ message: string }>) => {
      if (error?.response?.status) {
        switch (error.response.status) {
          case 403:
            navigate(ROUTES.home);
            showPopup({
              message: error.response.data.message,
              type: 'ERROR',
            });
            break;
          case 401:
            await handle401Error(error, navigate);
            break;
          case 429:
            showPopup({
              message: error.response.data.message,
              type: 'ERROR',
            });
            break;
          default:
            showPopup({
              message: error.response.data.message,
              type: 'ERROR',
            });
            captureException(error);
            break;
        }
      }

      // Capture the error in Sentry
      captureException(error);
    },
    [navigate, formatMessage],
  );

  useLayoutEffect(() => {
    tzAxios.defaults.headers.common['X-Custom-Language'] = 'ar';
    tzAxios.defaults.headers.common.Authorization = authHeader();

    queryClient.invalidateQueries();

    const interceptorId = tzAxios.interceptors.response.use(handleResponseSuccess, (error) => {
      handleErrorCodes(error);
      return Promise.reject(error);
    });

    return () => {
      tzAxios.interceptors.response.eject(interceptorId);
    };
  }, [authHeader, queryClient, handleErrorCodes]);
};

async function handle401Error(error: AxiosError, navigate: (path: string) => void) {
  const refreshToken = localStorage.getItem('_auth_refresh');
  if (refreshToken) {
    try {
      const accessToken = await refreshAccessToken(refreshToken);
      if (accessToken && error.config) {
        updateAccessTokenAndHeaders(accessToken, error.config);
        if (!(error.config.url && error.config.url.endsWith('/checkout/'))) {
          location.reload();
          return tzAxios(error.config as AxiosRequestConfig);
        }
        return tzAxios(error.config as AxiosRequestConfig);
      }
    } catch (err) {
      captureException(err);
      console.error('Error refreshing token', err);
    }
  }

  navigate(ROUTES.home);
}

async function refreshAccessToken(refreshToken: string) {
  const data = { refreshToken };
  const response = await apiRefresh(data);
  return response.accessToken;
}

function updateAccessTokenAndHeaders(accessToken: string, config: AxiosRequestConfig) {
  localStorage.setItem('_auth', accessToken);
  tzAxios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  if (config && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
}
